import { SALES_FUNNEL_MAIN_ORDER, normalizeFunnelStageKey } from '@/constants/salesFunnel'

/** Valor acumulado por etapa na ordem do fluxo comercial (topo → GANHO). */
export function buildCumulativeFunnelSeries(
  stages: { stage: string; value: number }[],
): number[] {
  const byStage = new Map<string, number>()
  for (const row of stages) {
    const key = normalizeFunnelStageKey(row.stage)
    byStage.set(key, (byStage.get(key) ?? 0) + row.value)
  }
  let cumulative = 0
  return SALES_FUNNEL_MAIN_ORDER.map((stage) => {
    cumulative += byStage.get(stage) ?? 0
    return cumulative
  })
}

/** Série mínima para sparkline (2+ pontos). */
export function ensureTrendSeries(values: number[], minPoints = 8): number[] {
  const cleaned = values.filter((v) => Number.isFinite(v))
  if (cleaned.length >= 2) return cleaned
  if (cleaned.length === 1) return buildTrendFromValue(cleaned[0], minPoints)
  return buildTrendFromValue(0, minPoints)
}

/** Escolhe a melhor série disponível para o KPI Pipeline. */
export function resolvePipelineTrendSeries(
  monthlyRevenue: number[],
  dealsByStage: { stage: string; value: number }[],
  pipelineValue: number,
): number[] {
  const revenue = monthlyRevenue.filter((v) => Number.isFinite(v))
  if (revenue.length >= 2 && Math.max(...revenue) - Math.min(...revenue) > 0) {
    return revenue
  }

  const funnel = buildCumulativeFunnelSeries(dealsByStage)
  if (funnel.length >= 2 && Math.max(...funnel) - Math.min(...funnel) > 0) {
    return funnel
  }

  return buildTrendFromValue(pipelineValue, 10)
}

/** Interpola até o valor final (fallback quando não há histórico mensal). */
export function buildTrendFromValue(total: number, points = 12): number[] {
  const target = Math.max(0, total)
  if (target === 0) return Array.from({ length: points }, () => 0)
  return Array.from({ length: points }, (_, i) => {
    const progress = points === 1 ? 1 : i / (points - 1)
    const wave = Math.sin(progress * Math.PI) * 0.12
    return Math.round(target * (0.55 + progress * 0.45 + wave))
  })
}

/** Escala Y com padding para evitar linha horizontal quando a variação é pequena. */
export function getSparklineYBounds(data: number[]): { min: number; max: number } {
  const min = Math.min(...data)
  const max = Math.max(...data)
  const span = max - min

  if (span <= 0) {
    const base = max || 1
    return { min: base * 0.7, max: base * 1.05 }
  }

  const pad = Math.max(span * 0.15, max * 0.05)
  return { min: min - pad, max: max + pad }
}

export function trendChangePercent(series: number[]): number | null {
  if (series.length < 2) return null
  const first = series[0]
  const last = series[series.length - 1]
  if (first === 0) return last > 0 ? 100 : 0
  return Math.round(((last - first) / Math.abs(first)) * 1000) / 10
}

export function trendIsPositive(series: number[]): boolean {
  if (series.length < 2) return true
  return series[series.length - 1] >= series[0]
}

export function seriesHasVariation(series: number[]): boolean {
  if (series.length < 2) return false
  return Math.max(...series) - Math.min(...series) > 0
}
